// What a new version of an installed app changes, for the person approving it (LO-203).
//
// A request for an app that already has an approved manifest replaces that manifest
// wholesale, and a whole document hides a one-word change (LO-128 found that the hard
// way, on the ceiling). A second version also brings new code. So the approver is shown
// two differences, and both are computed here, as plain functions a test can hold:
//
// - the ceiling against the manifest in force: every grant, verb, agent and host added
//   (`+`), removed (`-`) or changed (`~`), from the kernel's own documents
// - the code against the build that is live: the files the request's build was built
//   from against the files of the build whose release the running install pins, line by
//   line, the added lines marked
//
// What neither proves: that the change is safe. The ceiling diff says what the app may
// newly read or reach, not what it will do with it; the code diff is of the sources a build
// was made from, and says nothing about dependencies the build resolved.

const AXES = [
  ["reads", "Reads"],
  ["service_reads", "Reads with no user"],
];
const VERB_AXES = [
  ["actions", "Actions"],
  ["service_actions", "Actions with no user"],
];

const tagsSaid = (tags) => (tags && tags.length ? [...tags].sort().join(", ") : "untagged only");
const verbOf = (a) => (typeof a === "string" ? a : a?.verb);

/**
 * The ceiling `proposed` against `current` (the approved manifest, or null for a first
 * version). `lines` is in axis order, each `{sign, axis, text}`; `widens` is whether
 * anything was added, including a tag added to a type already granted.
 */
export function ceilingDiff(current, proposed) {
  const lines = [];
  let widens = false;
  for (const [key, axis] of AXES) {
    const was = new Map((current?.[key] || []).map((g) => [g.type, g.tags || []]));
    const now = new Map((proposed?.[key] || []).map((g) => [g.type, g.tags || []]));
    for (const [type, tags] of now) {
      if (!was.has(type)) {
        lines.push({ sign: "+", axis, text: `${type} (${tagsSaid(tags)})` });
        widens = true;
      } else if (tagsSaid(was.get(type)) !== tagsSaid(tags)) {
        lines.push({ sign: "~", axis, text: `${type} ${tagsSaid(was.get(type))} → ${tagsSaid(tags)}` });
        const before = new Set(was.get(type));
        if (tags.some((t) => !before.has(t)) || (before.size && !tags.length)) widens = true;
      }
    }
    for (const type of was.keys()) {
      if (!now.has(type)) lines.push({ sign: "-", axis, text: `${type} (${tagsSaid(was.get(type))})` });
    }
  }
  for (const [key, axis] of VERB_AXES) {
    const was = new Set((current?.[key] || []).map(verbOf));
    const now = new Set((proposed?.[key] || []).map(verbOf));
    for (const v of now) if (!was.has(v)) { lines.push({ sign: "+", axis, text: v }); widens = true; }
    for (const v of was) if (!now.has(v)) lines.push({ sign: "-", axis, text: v });
  }
  const agents = (list) => new Map((list || []).map((a) => [a.name, a]));
  const wasA = agents(current?.agents);
  const nowA = agents(proposed?.agents);
  for (const [name, a] of nowA) {
    if (!wasA.has(name)) { lines.push({ sign: "+", axis: "Agents", text: name }); widens = true; }
    else if ((wasA.get(name).instructions || "") !== (a.instructions || "")) {
      lines.push({ sign: "~", axis: "Agents", text: `${name}: instructions change` });
    }
  }
  for (const name of wasA.keys()) if (!nowA.has(name)) lines.push({ sign: "-", axis: "Agents", text: name });
  // Hosts are part of what an approval records (LO-175): a new one widens what the
  // running app can reach, however narrow its reads are.
  const wasH = new Set(current?.egress || []);
  const nowH = new Set(proposed?.egress || []);
  for (const host of nowH) if (!wasH.has(host)) { lines.push({ sign: "+", axis: "Egress", text: host }); widens = true; }
  for (const host of wasH) if (!nowH.has(host)) lines.push({ sign: "-", axis: "Egress", text: host });
  return {
    lines,
    widens,
    first: !current,
    added: lines.filter((l) => l.sign === "+").length,
    removed: lines.filter((l) => l.sign === "-").length,
    changed: lines.filter((l) => l.sign === "~").length,
  };
}

const MAX_CELLS = 2_000_000;

function split(text) {
  const lines = String(text ?? "").split("\n");
  if (lines.length && lines[lines.length - 1] === "") lines.pop();
  return lines;
}

/**
 * A line diff of `before` and `after`: `[{sign, text}]` with `sign` `" "`, `"-"` or `"+"`,
 * removed lines before the lines that replace them. Longest common subsequence, so it is
 * exact and quadratic; `null` when the two are too large to diff that way.
 */
export function lineDiff(before, after) {
  const a = split(before);
  const b = split(after);
  if ((a.length + 1) * (b.length + 1) > MAX_CELLS) return null;
  const w = b.length + 1;
  const lcs = new Uint32Array((a.length + 1) * w);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i * w + j] = a[i] === b[j]
        ? lcs[(i + 1) * w + j + 1] + 1
        : Math.max(lcs[(i + 1) * w + j], lcs[i * w + j + 1]);
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ sign: " ", text: a[i] });
      i += 1;
      j += 1;
    } else if (lcs[(i + 1) * w + j] >= lcs[i * w + j + 1]) {
      out.push({ sign: "-", text: a[i] });
      i += 1;
    } else {
      out.push({ sign: "+", text: b[j] });
      j += 1;
    }
  }
  while (i < a.length) out.push({ sign: "-", text: a[i++] });
  while (j < b.length) out.push({ sign: "+", text: b[j++] });
  return out;
}

/**
 * The files of the live build against the files of the new one (`[{path, contents}]`
 * each). Unchanged files are left out. A changed file too large to diff line by line is
 * `tooLarge` with no lines, rather than a diff that is wrong.
 */
export function sourceDiff(liveFiles, newFiles) {
  const was = new Map((liveFiles || []).map((f) => [f.path, String(f.contents ?? "")]));
  const now = new Map((newFiles || []).map((f) => [f.path, String(f.contents ?? "")]));
  const paths = [...new Set([...was.keys(), ...now.keys()])].sort();
  const files = [];
  for (const path of paths) {
    const before = was.get(path);
    const after = now.get(path);
    if (before === after) continue;
    const status = before === undefined ? "added" : after === undefined ? "removed" : "changed";
    const lines = lineDiff(before ?? "", after ?? "");
    if (lines === null) {
      files.push({ path, status, added: 0, removed: 0, lines: [], tooLarge: true });
      continue;
    }
    files.push({
      path,
      status,
      added: lines.filter((l) => l.sign === "+").length,
      removed: lines.filter((l) => l.sign === "-").length,
      lines,
      tooLarge: false,
    });
  }
  return {
    files,
    added: files.reduce((n, f) => n + f.added, 0),
    removed: files.reduce((n, f) => n + f.removed, 0),
  };
}

/**
 * Which build is live for `app`: the running install (never a sandbox, never one being
 * removed), the release it pins, and the build that release was published from.
 * `instances` is the control plane's list and `builds` is `GET /builds/{app}`'s; either
 * `null` means it could not be read, and the answer is then `known: false`, never a guess.
 * A release no build here made (a template app, or one older than the builds table) has
 * no `buildId`, and there is then no code to compare against.
 */
export function liveBuildOf({ app, instances, builds }) {
  if (!Array.isArray(instances) || !Array.isArray(builds)) {
    return { instance: null, version: null, buildId: null, known: false };
  }
  const live = instances.find(
    (i) => i?.app === app && !i.sandbox && (i.desiredState || "present") !== "absent",
  );
  if (!live) return { instance: null, version: null, buildId: null, known: true };
  const version = live.packageVersion || null;
  const build = version ? builds.find((b) => b?.published_version === version) : null;
  return { instance: live.name, version, buildId: build?.build_id || null, known: true };
}
